// src/components/IntroView.tsx
import {
  Capsule,
  LayerName,
  LAYER_LABELS,
  getAvailableLayers,
  getLayerVideoPath,
} from '../types/capsule';
import { VideoPlayer } from './VideoPlayer';
import '../styles/IntroView.css';

interface IntroViewProps {
  capsule: Capsule;
  onStartLearning: () => void;
}

export function IntroView({ capsule, onStartLearning }: IntroViewProps) {
  const { metadata } = capsule;
  const introContent = capsule.layers.intro;
  const videoUrl = getLayerVideoPath(capsule, 'intro');

  // Get non-intro layers that are available
  const availableLayers = getAvailableLayers(capsule).filter(l => l !== 'intro');

  return (
    <div className="intro-view">
      {/* Performance video */}
      {videoUrl && (
        <div className="intro-video">
          <VideoPlayer src={videoUrl} showControls />
        </div>
      )}

      {/* Song metadata */}
      <div className="intro-meta">
        <h1 className="intro-title">{metadata.title}</h1>

        {metadata.artist && (
          <span className="intro-artist">{metadata.artist}</span>
        )}

        {metadata.arranger && (
          <span className="intro-arranger">Arranged by {metadata.arranger}</span>
        )}

        {metadata.difficulty && (
          <div className="intro-difficulty">
            <span className="intro-difficulty-label">Difficulty</span>
            {[1, 2, 3, 4, 5].map((level) => (
              <span
                key={level}
                className={`intro-star ${level <= metadata.difficulty! ? 'intro-star--filled' : ''}`}
              >
                &#9733;
              </span>
            ))}
          </div>
        )}

        {metadata.tags && metadata.tags.length > 0 && (
          <div className="intro-tags">
            {metadata.tags.map((tag) => (
              <span key={tag} className="intro-tag">{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* Description */}
      {introContent?.description && (
        <p className="intro-description">{introContent.description}</p>
      )}

      {/* Available layers */}
      {availableLayers.length > 0 && (
        <div className="intro-layers">
          <span className="intro-layers-title">Available Layers</span>
          <div className="intro-layers-list">
            {availableLayers.map((layer: LayerName) => (
              <div key={layer} className="intro-layer-chip">
                <span className="intro-layer-chip-dot" />
                {LAYER_LABELS[layer]}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Start learning CTA */}
      {availableLayers.length > 0 && (
        <div className="intro-actions">
          <button className="intro-start-btn" onClick={onStartLearning}>
            Start Learning
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
